import { useState, useEffect, useRef } from "react";
import { API, useAuth, useTheme } from "@/App";
import axios from "axios";
import { Settings, X, MessageSquare, Heart, Shield, Gauge, Brain, Sun, Moon, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STYLE_OPTIONS = [
  { key: "adaptive", label: "Adaptive", desc: "Mirrors your energy" },
  { key: "casual", label: "Casual", desc: "Like texting a friend" },
  { key: "formal", label: "Formal", desc: "Professional tone" },
  { key: "poetic", label: "Poetic", desc: "Literary and expressive" },
  { key: "direct", label: "Direct", desc: "Straight to the point" },
];

const LENGTH_OPTIONS = [
  { key: "brief", label: "Brief", desc: "Short & punchy" },
  { key: "medium", label: "Medium", desc: "Balanced depth" },
  { key: "detailed", label: "Detailed", desc: "Thorough explanations" },
];

const SENSITIVITY_OPTIONS = [
  { key: "low", label: "Practical", desc: "Focus on solutions" },
  { key: "medium", label: "Balanced", desc: "Empathy + advice" },
  { key: "high", label: "High Empathy", desc: "Feelings-first approach" },
];

const HUMOR_OPTIONS = [
  { key: "none", label: "Serious", desc: "No humor" },
  { key: "moderate", label: "Moderate", desc: "Occasional wit" },
  { key: "high", label: "Witty", desc: "Frequent humor" },
];

export function QuickSettingsPanel({ open, onClose, onOpenMemory }) {
  const { token } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open) fetchPrefs();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  const fetchPrefs = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/ai-preferences`, { headers });
      setPrefs(data);
    } catch (err) {
      console.error("Failed to fetch AI prefs", err);
    }
  };

  const updatePref = async (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setSaving(true);
    try {
      await axios.put(`${API}/settings/ai-preferences`, { [key]: value }, { headers });
    } catch (err) {
      console.error("Failed to save pref", err);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="fixed right-0 top-0 bottom-0 z-50 w-[340px] max-w-[90vw] bg-card border-l border-border/50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200"
    >
      {/* Header */}
      <div className="sticky top-0 bg-card/95 backdrop-blur-md z-10 flex items-center justify-between p-4 border-b border-border/30">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-heading font-semibold">Quick Settings</h3>
          {saving && <span className="text-[10px] text-primary animate-pulse">Saving...</span>}
        </div>
        <button
          data-testid="quick-settings-close"
          onClick={onClose}
          className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {!prefs ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="p-4 space-y-5">
          {/* Quick Toggles */}
          <div className="flex items-center justify-between p-3 rounded-2xl bg-muted/30 border border-border/30">
            <div className="flex items-center gap-2">
              {darkMode ? <Moon className="w-4 h-4 text-primary" /> : <Sun className="w-4 h-4 text-amber-500" />}
              <span className="text-xs font-medium">{darkMode ? "Dark" : "Light"} Mode</span>
            </div>
            <button
              onClick={toggleDarkMode}
              className={cn("relative w-10 h-6 rounded-full transition-all", darkMode ? "bg-primary" : "bg-muted")}
            >
              <div className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all", darkMode ? "left-[18px]" : "left-0.5")} />
            </button>
          </div>

          {/* Communication Style */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold">Communication Style</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {STYLE_OPTIONS.map(s => (
                <button
                  key={s.key}
                  data-testid={`qs-style-${s.key}`}
                  onClick={() => updatePref("communication_style", s.key)}
                  className={cn(
                    "text-left px-3 py-2 rounded-xl border text-xs transition-all",
                    prefs.communication_style === s.key
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-border/40 hover:border-primary/30"
                  )}
                >
                  <p className="font-medium">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Response Length */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Gauge className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold">Response Length</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {LENGTH_OPTIONS.map(l => (
                <button
                  key={l.key}
                  data-testid={`qs-length-${l.key}`}
                  onClick={() => updatePref("response_length", l.key)}
                  className={cn(
                    "text-center px-2.5 py-2 rounded-xl border text-xs transition-all",
                    prefs.response_length === l.key
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-border/40 hover:border-primary/30"
                  )}
                >
                  <p className="font-medium">{l.label}</p>
                  <p className="text-[10px] text-muted-foreground">{l.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Emotional Sensitivity */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold">Emotional Sensitivity</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {SENSITIVITY_OPTIONS.map(s => (
                <button
                  key={s.key}
                  data-testid={`qs-sensitivity-${s.key}`}
                  onClick={() => updatePref("emotional_sensitivity", s.key)}
                  className={cn(
                    "text-center px-2.5 py-2 rounded-xl border text-xs transition-all",
                    prefs.emotional_sensitivity === s.key
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-border/40 hover:border-primary/30"
                  )}
                >
                  <p className="font-medium">{s.label}</p>
                  <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Humor */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-primary text-sm">&#9786;</span>
              <span className="text-xs font-semibold">Humor Level</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {HUMOR_OPTIONS.map(h => (
                <button
                  key={h.key}
                  data-testid={`qs-humor-${h.key}`}
                  onClick={() => updatePref("humor_level", h.key)}
                  className={cn(
                    "text-center px-2.5 py-2 rounded-xl border text-xs transition-all",
                    prefs.humor_level === h.key
                      ? "border-primary bg-primary/5 text-primary font-medium"
                      : "border-border/40 hover:border-primary/30"
                  )}
                >
                  <p className="font-medium">{h.label}</p>
                  <p className="text-[10px] text-muted-foreground">{h.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Memory Shortcut */}
          <button
            data-testid="qs-open-memory"
            onClick={() => { onClose(); onOpenMemory(); }}
            className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border/40 hover:border-primary/30 hover:bg-primary/[0.02] transition-all text-left"
          >
            <Brain className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">Omni's Memory</p>
              <p className="text-[10px] text-muted-foreground">See what Omni remembers about you</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </button>

          {/* Full Settings Link */}
          <button
            data-testid="qs-full-settings"
            onClick={() => { onClose(); navigate("/settings"); }}
            className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border/40 hover:border-primary/30 hover:bg-primary/[0.02] transition-all text-left"
          >
            <Settings className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold">All Settings</p>
              <p className="text-[10px] text-muted-foreground">Theme, voice, language, backup & more</p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
}
